<template>
<div>
    <div v-if="!ready">
        <div v-if="loadExtraNodes" class="alert alert-info" role="alert">Loading extra nodes</div>
        <i v-else class="fa fa-spinner fa-pulse" /> Please wait, the graph is loading.
    </div>
    <div v-else>
        <div class="data-lineage-toolbar">
            <toolbar
                :isExpanded="isExpanded"
                :status="lineageStatus"
                @zoom="zoom"
                @resize="resize"
                @click="manageLink"
                @reload="reload"
            />
        </div>
        <cytoscape
            ref="cy"
            id="cy"
            :key="reRenderKey"
            :config="config"
            :preConfig="preConfig"
            :afterCreated="afterCreated"

            @click="click"
            @mousedown="highlightEdge($event, true)"
            @mouseup="highlightEdge($event, false)"
            @drag="drag"
        >
        </cytoscape>

        <tippy
            :clickedNode="clickedNode"
            :clickedPathInfo="clickedPathInfo"
            :requestedData="requestedData"
        />
    </div>
    <alert v-if="error" type="danger">
        There was an error with the graph. Please try again later.
    </alert>
</div>
</template>

<script>
import Toolbar from '@/graphs/data_lineage/Toolbar.vue'
import Tippy from '@/graphs/data_lineage/Tippy.vue'

import apiRequest from 'src/mixins/api-request'
import pathsMixin from '@/graphs/data_lineage/pathsMixin.vue'
import tippyMixin from '@/graphs/data_lineage/tippyMixin.vue'

import fcose from "cytoscape-fcose"
import popper from 'cytoscape-popper'
import { register as htmlnode } from 'cytoscape-html-node'

import { hasElement } from 'src/lib/utils.js'
import { basicLayoutConfig, defaultLineageStatus, defaultFcoseLayout } from 'src/lib/graph/data-lineage/config.js'
import { alignColumns, alignRows, assignColumnLevel, updateColumn } from 'src/lib/graph/data-lineage/align-layout.js'
import { hideAllTippy, adjustSize, sentenceTrimmer, getConnectedProvenanceEdges, getConnectedLinkage } from 'src/lib/graph/data-lineage/utils.js'
import { highlightLineage, hideLineage, manageTableOpacity } from 'src/lib/graph/data-lineage/draw-edges.js'

export default {
    components: {
        Toolbar,
        Tippy
    },
    mixins: [apiRequest, pathsMixin, tippyMixin],
    props: {
        url: {
            type: String,
            required: true
        },
        generateUrl: {
            type: String,
            required: true
        },
        sourceUrl: {
            type: String,
            required: true
        }
    },
    data: () => ({
        ready: false,
        error: false,
        reRenderKey: 0,
        config: basicLayoutConfig,

        cytoscape: null,

        nodes: [],
        edges: [],
        fcoseLayout: null,
        disPathMap: {},
        // Path id mapping to path's property
        idPropertyMap: {},

        // Node which is going to render the tooltips
        clickedNode: {},
        clickedPathInfo: {},

        // Store requests to load extra nodes
        requestedData: [],
        newlyRequestedDataId: null,
        loadExtraNodes: false,
        hasUpdate: false,
        // Whether this element is expanded
        isExpanded: false,
        // The lineageStatus of lines displayed for each type [dataset, distribution, path]
        lineageStatus: {},
    }),
    mounted() {
        this.lineageStatus = JSON.parse(JSON.stringify(defaultLineageStatus))
        this.initialize()
    },
    methods: {
        preConfig(cytoscape) {
            cytoscape.use(fcose)
            cytoscape.use(popper)
            cytoscape.use(htmlnode)
            /* eslint-env node */
            var nodeHtmlLabel = require('cytoscape-node-html-label')
            cytoscape.use(nodeHtmlLabel)

            this.cytoscape = cytoscape

            cytoscape.use = () => {}
        },
        initialize: function(fromReload) {
            this.ready = false
            this.get(this.url).then((response) => {
                this.nodes = response.data.nodes
                this.edges = response.data.edges
                this.disPathMap = response.data.disPathMap
                this.idPropertyMap = response.data.idPropertyMap

                this.$set(this.config, 'elements', [...this.nodes, ...this.edges])
                this.hasUpdate = fromReload ? true : false
                this.ready = true
            })
        },
        // This is the callback when cytoscape.js is created
        afterCreated(cy) {
            // Update layout after adding elements
            if (this.hasUpdate) {
                cy.elements().remove()
                cy.add([...this.nodes, ...this.edges])
                this.$set(this.config, 'elements', [...this.nodes, ...this.edges])

                this.hasUpdate = false
                this.reRenderKey++
            }
            this.reBuildLayout(cy, false)

            // Add htmllabel to  dataset
            this.addPaths(cy)
            this.addHtmlLabel(cy)

            cy.nodes().nonorphans().on('grab', function(){ this.ungrabify() }).on('free', function(){ this.grabify() })

            // Highlight and hide elements according to lineage status
            for (const [nodeType, value] of Object.entries(this.lineageStatus)) {
                if (value['dotted']) {
                    highlightLineage(nodeType, false, cy)
                }
            }

            $('#cytoscape-div').css('border', '1px solid #ccc')

            // Expand to full size
            adjustSize(cy, this.isExpanded)
        },
        // Improve edge curves and points direction
        // Cubic bezier is referenced from https://github.com/cytoscape/cytoscape.js/issues/2579#issuecomment-562129322
        // which constructs a smooth bezier link
        adjustEdgeCurve(edge) {
            let x0 = edge.source().position("x")
            let x1 = edge.target().position("x")
            const y0 = edge.source().boundingbox().y1
            const y1 = edge.target().boundingbox().y1
            const x = x1 - x0
            const y = y1 - y0
            const z = Math.sqrt(x * x + y * y)
            const costheta = x / z
            let alpha = 0.4
            let controlPointDistances = [-alpha * y * costheta, alpha * y * costheta]
            let controlPointWeights = [alpha, 1-alpha]

            // Change endpoints
            let sourceEndpointY = ''
            let targetEndpointY = ''
            let sourceX = "50%"
            let targetX = "50%"
            if (Math.abs(x1 - x0) < edge.source().width() ) {
                // Target and source are vertical
                sourceX = "50%"
                targetX = "50%"
                if (y1 > y0) {
                    controlPointDistances = [-200]
                } else {
                    controlPointDistances = [200]
                }
                controlPointWeights = [0.75]
            } else if (x1 <= x0) {
                // Target is on the left of the source
                sourceX = "-50%"
                targetX = "50%"
                x1 = edge.target().boundingbox().x1
            } else {
                // Target is on the right of the source
                sourceX = "50%"
                targetX = "-50%"
                x0 = edge.source().boundingbox().x1
            }
            sourceEndpointY = edge.style()['source-endpoint'].split(" ")[1]
            targetEndpointY = edge.style()['target-endpoint'].split(" ")[1]
            edge.style({
                'source-endpoint': sourceX + " " + sourceEndpointY,
                'target-endpoint': targetX + " "  + targetEndpointY,
                'control-point-distances': controlPointDistances,
                'control-point-weights': controlPointWeights
            })
        },

        // Rebuild layout, to put source to the left of main dataset, generated to the right of main dataset
        // Show paths and distributions in a list
        reBuildLayout(cy, triggeredFromExpand, zoomLevel) {
            let fcoseLayout = Object.assign({}, defaultFcoseLayout)

            // Add column id to each column for tracking
            // Base (main) dataset's column is 0, nodes on the left will be decreased by 1, nodes on the right will be increased by 1
            assignColumnLevel(cy.nodes('.baseSourceNode'), -1)
            assignColumnLevel(cy.nodes('.baseNode'), 0)
            assignColumnLevel(cy.nodes('.baseGeneratedNode'), 1)

            // Make layout for the newly added data
            // Asign column level to newly added data if there are any
            // Run old extra nodes first to update column, and then run newly added extra nodes to priotise its hierarchy
            for (let index = this.requestedData.length - 2; index >= 0; index--) {
                let newData = this.requestedData[index]
                updateColumn(cy, newData)
            }
            let newData = this.requestedData[this.requestedData.length -1]
            if (newData) {
                updateColumn(cy, newData)
            }

            // Readjust gap and layout for nodes in each column
            let constraint = alignColumns(cy, 0, [], {vertical: [], horizontal: []}, 'right')
            constraint = alignColumns(cy, -1, constraint.relativePlacementConstraint, constraint.alignmentConstraint, 'left')
            fcoseLayout['relativePlacementConstraint'] = constraint['relativePlacementConstraint']
            fcoseLayout['alignmentConstraint'] = constraint['alignmentConstraint']

            // Align rows
            fcoseLayout['relativePlacementConstraint'] = alignRows(cy, 0, fcoseLayout['relativePlacementConstraint'], 'right')
            fcoseLayout['relativePlacementConstraint'] = alignRows(cy, 0, fcoseLayout['relativePlacementConstraint'], 'left')

            this.fcoseLayout = fcoseLayout
            if (triggeredFromExpand) {
                fcoseLayout['animate'] = false
            }

            let layout = cy.layout(fcoseLayout)
            layout.on('layoutready', () => {
                cy.nodes('.clonedNode').style({
                    'display': 'none'
                })
                // Apply layout changes after animation, otherwise positions are not finalised
                if (!triggeredFromExpand) {
                    setTimeout(() => this.rebuildClonedData(cy, triggeredFromExpand, zoomLevel), 500)
                } else {
                    this.rebuildClonedData(cy, triggeredFromExpand, zoomLevel)
                }
            })
            layout.run()
        },
        rebuildClonedData(cy, triggeredFromExpand, zoomLevel) {
            this.positionClones(cy.nodes('.clonedNode'), cy)
            this.positionVirtualPathNode(this.expandedDis, cy)

            // Collapse datasets in distribution
            if (this.lineageStatus['datasets']['collapseDataset']) {
                this.collapseDataset(true)
            }
            // Expand distributions if it's set in toolbar
            // Don't run this if this rebuild is from expand click
            if (!triggeredFromExpand && !this.lineageStatus['distributions']['collapseDistribution']) {
                this.collapseAllDistributions(false, true)
            }
            // Highlight newly added extra nodes
            if (this.newlyRequestedDataId) {
                let dataClass = this.requestedData[this.requestedData.length - 1] + 'Node'
                cy.nodes(`.${dataClass}`).addClass('highlighted')
                cy.nodes(`#${this.newlyRequestedDataId}`).addClass('highlighted')
            }
            setTimeout(() => {
                cy.nodes(`.highlighted`).removeClass('highlighted')
            }, 3000)

            if (triggeredFromExpand) {
                cy.zoom({
                    level: zoomLevel,
                    position: cy.nodes(`#${this.collapsedDisId}`).position()
                })
            }
        },
        // Position cloned node in the same position of node, in order to create nice curve
        positionClones(nodes, cy) {
            nodes.forEach(node => {
                // Make cloned node undraggable
                node.ungrabify()
                let clonedNode = cy.nodes(`#${node.data().clonedData}`)
                if (clonedNode.data()) {
                    // Position path cloned node after the distribution is expanded
                    this.positionClonedNode(clonedNode, node)
                }
            })
        },
        positionClonedNode(clonedNode, node) {
            let clonedX = clonedNode.position('x')
            let clonedY = clonedNode.height() == 15 ? clonedNode.position('y') : clonedNode.position('y') - clonedNode.data().height/2 + 8
            if (node.classes().indexOf('datasetClonedNode') != -1) {
                clonedY = clonedNode.position('y') - clonedNode.height()/2 - 18
            }
            node.position({
                x: clonedX,
                y: clonedY
            })
            node.style({
                'display': 'element',
                'opacity': 0
            })
            node.connectedEdges(edge => {
                this.adjustEdgeCurve(edge)
            })
        },

        // Rerun layout after collapsing or expanding
        reRunLayout(cy, fromExpand) {
            let previouszoom = cy.zoom()
            this.newlyRequestedDataId = null
            cy.nodes('.pathVirtualNode').style({
                'display': 'none'
            })
            cy.edges('.collapsedEdge').style({
                'display': 'element'
            })
            this.reBuildLayout(cy, fromExpand, previouszoom)
        },

        // Request to show more data or control path edges
        addMoreData(id, action) {
            hideAllTippy()
            let apiUrl = this.getApiUrl(id, action)
            this.newlyRequestedDataId = id
            let requestedNode = this.$refs.cy.instance.nodes(`#${id}`)
            this.get(apiUrl).then((response) => {
                // Only add node/edge which is not in the existed list to prevent duplicate
                // Replace existing node if it's not base node, to add more hierarchy
                response.data.nodes.forEach(node => {
                    if (!this.nodes.some(existedNode => {return existedNode.data.id.toString() === node.data.id.toString()})) {
                        this.nodes.push(node)
                    } else if (String(node.data.id) != String(id) && requestedNode.data().parent != node.data.id) {
                        // Only replace node which is not baseNode, to prevent overlaps
                        let index = this.nodes.findIndex(x => x['data']['id'].toString() === node['data']['id'].toString())
                        let existedNode = this.nodes[index]
                        if (!existedNode['classes'].includes('baseNode')
                            && (!('parent' in existedNode['data']))) {
                            this.nodes[index] = node
                        }
                    }
                })
                response.data.edges.forEach(edge => {
                    if (!this.edges.some(existedEdge => {return existedEdge.data.id.toString() === edge.data.id.toString()})) {
                        this.edges.push(edge)
                    }
                })
                this.disPathMap = Object.assign(this.disPathMap, response.data.disPathMap)
                this.idPropertyMap = Object.assign(this.idPropertyMap, response.data.idPropertyMap)

                // Update buttons in the tooltip
                let index = this.nodes.findIndex(item => item.data.id.toString() === id.toString())
                if (index != -1) {
                    if (action == "source") {
                        this.nodes[index].data.hasSource = false
                    } else {
                        this.nodes[index].data.hasGenerated = false
                    }
                }

                this.hasUpdate = true
                this.loadExtraNodes = false
                this.ready = true
            })
        },

        // Click to highlight elements, collapse and show help
        click(event) {
            if ('id' in event.target ) {
                // Highlight elements
                let datasetElementClasses = ['dataset', 'datasetEdge', 'datasetClonedNode']
                let distributionElementClasses = ['distribution', 'distributionEdge', 'distributionClonedNode']
                let highlightedClassMap = {
                    'datasets': false,
                    'distributions': false,
                    'paths': false
                }
                if (hasElement(event.target.classes(), datasetElementClasses)) {
                    highlightedClassMap['datasets'] = true
                } else if (hasElement(event.target.classes(), distributionElementClasses)) {
                    highlightedClassMap['distributions'] = true
                    this.handleDistributionClick(event)
                } else {
                    highlightedClassMap['paths'] = true
                    this.handleDistributionClick(event)
                }
                for (const [nodeType, highlight] of Object.entries(highlightedClassMap)) {
                    highlightLineage(nodeType, highlight, this.$refs.cy.instance)
                    this.lineageStatus[nodeType]['dotted'] = highlight ? false : true
                    this.lineageStatus[nodeType]['dashed'] = highlight ? true : false
                }
                manageTableOpacity(this.lineageStatus)
            }
            this.triggerTippy(event)
        },

        // Get api rul for adding more data
        getApiUrl(id, type) {
            return type === 'source' ? this.sourceUrl.replace(0, id) : this.generateUrl.replace(0, id)
        },

        // Click one of provenance's paths, highlight all the relevant path
        // Also add tippy to edge
        highlightEdge(event, highlight) {
            if ('id' in event.target) {
                let connectedEdges = null
                if (event.target.data().provenance) {
                    connectedEdges = getConnectedProvenanceEdges(event, this.$refs.cy.instance)
                } else if (event.target.isEdge()) {
                    connectedEdges = getConnectedLinkage(event, this.$refs.cy.instance)
                }

                if (connectedEdges && event.target.opacity != 0) {
                    highlight ? connectedEdges.addClass('highlighted') : connectedEdges.removeClass('highlighted')
                }
                if (event.target.opacity != 0 && event.target.isEdge()) {
                    if (highlight) {
                        this.addTippyToEdge(event.target)
                    } else {
                        this.removeTippy()
                    }
                }
            }
        },

        // Make label width smaller than node
        addHtmlLabel(cy) {
            let nodeHtmlLabels = []
            cy.nodes('.dataset').forEach(node => {
                let _this = this
                let nodeHtmlLabel = {
                    query: `#${node.data().id}`,
                    valign: "left",
                    halign: "center",
                    valignBox: "left",
                    halignBox: "center",
                    tpl: function(data) {
                        return `<p id=${data.id + 'label'} class="datasetLabel" style="width: ${data.width}; opacity: ${_this.lineageStatus['datasets']['dotted'] ? 0.7 : 1}">
                                    <span>${sentenceTrimmer(data.name, 60, 45, 15)}</span>
                                    <i class="fas fa-question-circle helpIcon pull-right" data-node=${data.id} data-tippy-content="Tooltip"></i>
                                </p>`
                    }
                }
                nodeHtmlLabels.push(nodeHtmlLabel)
            })
            cy.nodeHtmlLabel(nodeHtmlLabels, {enablePointerEvents: true})
        },

        // When drag distribution and path node, move invisible nodes with nodes
        drag(event) {
            // Readjust label width
            let node = event.target
            if (node.classes().indexOf('distribution') != -1) {
                if (this.expandedDis.indexOf(Number(node.data().id)) != -1) {
                    this.handlePathDrag(node)
                }
            }

            let nodeClone = this.$refs.cy.instance.nodes(`#${node.data().id}cloned`)
            this.positionClonedNode(node, nodeClone)

            if (!this.lineageStatus['datasets']['collapseDataset']) {
                if (node.parent().length) {
                    let parentClone = this.$refs.cy.instance.nodes(`#${node.parent().data().id}cloned`)
                    this.positionClonedNode(node.parent(), parentClone)
                }
                if (node.children().length) {
                    node.children().forEach(child => {
                        let childClone = this.$refs.cy.instance.nodes(`#${child.data().id}cloned`)
                        this.positionClonedNode(child, childClone, true)

                        if (this.expandedDis.indexOf(Number(child.data().id)) != -1) {
                            this.handlePathDrag(child)
                        }
                    })
                }
            }
        },

        // Events from toolbar
        reload() {
            this.lineageStatus = JSON.parse(JSON.stringify(defaultLineageStatus))
            this.requestedData = []
            this.newlyRequestedDataId = null
            this.expandedDis = []
            this.initialize(true)
        },
        // Zoom in or zoom out control
        zoom(type) {
            let zoomLevel = type === 'increase' ? this.$refs.cy.instance.zoom() + 0.1 : this.$refs.cy.instance.zoom() - 0.1
            this.$refs.cy.instance.zoom(zoomLevel)
        },
        // Expand or shrink graph size
        resize() {
            this.$emit('resize', this.isExpanded ? 'shrink' : 'expand')
            this.isExpanded = !this.isExpanded

            adjustSize(this.$refs.cy.instance, this.isExpanded)
        },
        // Control the visibility of links and nodes triggered from toolbar
        manageLink(fieldName, action) {
            this.lineageStatus[fieldName][action] = !this.lineageStatus[fieldName][action]
            let lineMap = {'dashed': 'dotted', 'dotted': 'dashed'}
            this.lineageStatus[fieldName][lineMap[action]] = !this.lineageStatus[fieldName][action]

            if (action == 'hide') {
                hideLineage(fieldName, this.lineageStatus[fieldName][action], this.$refs.cy.instance)
            } else if (action == 'collapseDataset') {
                this.collapseDataset(this.lineageStatus[fieldName][action])
            } else if (action == 'collapseDistribution') {
                this.collapseAllDistributions(this.lineageStatus[fieldName][action])
            } else {
                highlightLineage(fieldName, this.lineageStatus[fieldName]['dashed'], this.$refs.cy.instance)
            }

            manageTableOpacity(this.lineageStatus)
        },
        // Collapse dataset, and hide distributions
        collapseDataset(collapse) {
            let cy = this.$refs.cy.instance
            // Collapse distribution
            this.$refs.cy.instance.nodes('.distribution, .distributionClonedNode').forEach(node => {
                node.style({
                    'display': collapse ? 'none' : 'element',
                    'height': collapse ? 0 : node.data().height
                })
            })
            // Collapse paths
            document.getElementsByClassName('htmlCard').forEach(card => {
                card.parentNode.style.display = collapse ? 'none' : 'block'
            })

            // Reposition nodes and edges
            if (!collapse) {
                this.positionClones(cy.nodes('.clonedNode'), cy)
                this.positionVirtualPathNode(this.expandedDis, cy)
            } else {
                this.$refs.cy.instance.nodes('.pathVirtualNode').style({'display': 'none'})
                this.$refs.cy.instance.nodes('.dataset').forEach(node => {
                    let nodeClone = this.$refs.cy.instance.nodes(`#${node.data().id}cloned`)
                    this.positionClonedNode(node, nodeClone)
                })
            }
        },
    }
}
</script>

<style scoped>
.data-lineage-toolbar {
    margin-bottom: 2px
}
</style>
